import { useEffect, useState } from 'react';
import { getPostHog } from 'utils/deferredAnalytics';

const TEST_VARIANT = 'test';

const readDevOverride = (flagKey) => {
  try {
    if (typeof window === 'undefined') return null;
    const override = window.localStorage?.getItem(`flag_${flagKey}`);
    if (override === 'true') return true;
    if (override === 'false') return false;
  } catch (e) {
    // localStorage unavailable
  }
  return null;
};

const variantIsEnabled = (variant) =>
  variant === TEST_VARIANT || variant === true;

// `options.ready` gates evaluation until the person properties required by the
// flag's release conditions (e.g. mortgage_country) are set on PostHog. While
// `ready` is false the hook returns `undefined` and never calls getFeatureFlag.
export const usePostHogFlag = (flagKey, options = {}) => {
  const { ready = true } = options;

  const [value, setValue] = useState(() => {
    const override = readDevOverride(flagKey);
    return override === null ? undefined : override;
  });

  useEffect(() => {
    const override = readDevOverride(flagKey);
    if (override !== null) {
      setValue(override);
      return undefined;
    }

    if (!ready) return undefined;

    const posthog = getPostHog();
    if (!posthog) return undefined;

    // Silent read of whatever PostHog currently has cached (no
    // $feature_flag_called event). Drives the initial UI value without
    // logging a possibly-stale capture — e.g. a value persisted in
    // localStorage from a previous session that didn't yet have
    // mortgage_country set.
    const cached = posthog.getFeatureFlag(flagKey, { send_event: false });
    if (cached !== undefined) {
      setValue(variantIsEnabled(cached));
    }

    // Capture $feature_flag_called exactly once, after the next reload —
    // at which point the value reflects our current person properties
    // (environment, mortgage_country) and identified user.
    let hasCaptured = false;
    const handleFlags = () => {
      const variant = posthog.getFeatureFlag(
        flagKey,
        hasCaptured ? { send_event: false } : undefined
      );
      if (variant === undefined) return;
      hasCaptured = true;
      setValue(variantIsEnabled(variant));
    };

    const unsubscribe = posthog.onFeatureFlags(handleFlags);

    // Force a reload now that prerequisites are in place. Debounced 5ms in
    // posthog-js, so it coalesces with the auto-fetch from init and any
    // sibling property updates made during the same render tick.
    posthog.reloadFeatureFlags();

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [flagKey, ready]);

  return value;
};

export default usePostHogFlag;
